/**
 * tool-capsule-match.ts
 * 工具调用胶囊的语义匹配规则。
 * 根据 tool name + input 判断是否命中特殊样式，返回增强的显示信息。
 */

import type { LucideIcon } from 'lucide-react';
import { Search, Download, Globe, Pen, BookOpen, Zap, FilePen, Terminal } from 'lucide-react';

/** 特殊胶囊类型 */
export type CapsuleMatchType =
	| 'skill-use'
	| 'skill-find'
	| 'skill-install'
	| 'skill-community'
	| 'file-read'
	| 'file-write'
	| 'file-rules'
	| 'file-memory'
	| 'file-soul'
	| 'file-rules-read'
	| 'file-memory-read'
	| 'file-soul-read'
	| 'bash-run';

export interface CapsuleMatch {
	type: CapsuleMatchType;
	/** 覆盖 label 显示文案 */
	label: string;
	/** 覆盖 paramLabel（从 input 提取关键词） */
	paramLabel?: string;
	/** 对应的 lucide 图标 */
	icon: LucideIcon;
}

const KEYWORD_RE = /keyword=([^&\s"']*)/;

/**
 * 尝试匹配特殊胶囊样式。
 * 匹配失败返回 undefined，使用默认样式。
 */
export function matchToolCapsule(toolName: string, toolInput: Record<string, unknown>): CapsuleMatch | undefined {
	// ── Skill 工具 ──
	if (toolName === 'Skill') {
		const skill = toolInput.skill as string | undefined;
		const args = toolInput.args as string | undefined;

		if (skill === 'find_skill') {
			return {
				type: 'skill-find',
				label: '查找技能',
				paramLabel: args || undefined,
				icon: Search,
			};
		}

		if (skill === 'install_skill') {
			return {
				type: 'skill-install',
				label: '安装技能',
				paramLabel: args || undefined,
				icon: Download,
			};
		}

		// 其他 Skill 调用 → 使用技能
		return {
			type: 'skill-use',
			label: '使用技能',
			paramLabel: args || undefined,
			icon: Zap,
		};
	}

	// ── Bash ──
	if (toolName === 'Bash') {
		const command = toolInput.command as string | undefined;

		// 社区搜索特殊匹配
		if (command?.includes('/skill/api/v1/list')) {
			const kwMatch = command.match(KEYWORD_RE);
			const keyword = kwMatch?.[1] ? decodeURIComponent(kwMatch[1]) : undefined;
			return {
				type: 'skill-community',
				label: '搜索Skill社区',
				paramLabel: keyword || undefined,
				icon: Globe,
			};
		}

		// 其他 Bash → 运行命令
		return {
			type: 'bash-run',
			label: '运行命令',
			paramLabel: command ? (command.length > 60 ? `${command.slice(0, 60)}...` : command) : '',
			icon: Terminal,
		};
	}

	// ── Write（非特殊文件）→ 更新文件 ──
	if (toolName === 'Write') {
		const filePath = toolInput.file_path as string | undefined;
		const fileName = filePath?.split('/').pop() ?? '';
		// 特殊文件走下方 Edit/Write 分支
		if (fileName === 'CLAUDE.md' || fileName === 'memory.md' || fileName === 'soul.md') {
			// fall through to the Edit/Write block below
		} else {
			return {
				type: 'file-write',
				label: '更新文件',
				paramLabel: fileName || '',
				icon: FilePen,
			};
		}
	}

	// ── Read 特殊文件 ──
	if (toolName === 'Read') {
		const filePath = toolInput.file_path as string | undefined;
		if (!filePath) return undefined;

		const fileName = filePath.split('/').pop() ?? '';

		if (fileName === 'CLAUDE.md') {
			return { type: 'file-rules-read', label: '读取工作守则', paramLabel: '', icon: BookOpen };
		}
		if (fileName === 'memory.md') {
			return { type: 'file-memory-read', label: '读取助理记忆', paramLabel: '', icon: BookOpen };
		}
		if (fileName === 'soul.md') {
			return { type: 'file-soul-read', label: '读取助理人格', paramLabel: '', icon: BookOpen };
		}

		// 其他 Read → 读取文件
		return {
			type: 'file-read',
			label: '读取文件',
			paramLabel: fileName || '',
			icon: BookOpen,
		};
	}

	// ── Edit / Write 特殊文件 ──
	if (toolName === 'Edit' || toolName === 'Write') {
		const filePath = toolInput.file_path as string | undefined;
		if (!filePath) return undefined;

		const fileName = filePath.split('/').pop() ?? '';

		if (fileName === 'CLAUDE.md') {
			return {
				type: 'file-rules',
				label: '更新工作守则',
				paramLabel: summarizeEdit(toolInput) ?? '',
				icon: Pen,
			};
		}

		if (fileName === 'memory.md') {
			return {
				type: 'file-memory',
				label: '更新助理记忆',
				paramLabel: summarizeEdit(toolInput) ?? '',
				icon: Pen,
			};
		}

		if (fileName === 'soul.md') {
			return {
				type: 'file-soul',
				label: '更新助理人格',
				paramLabel: summarizeEdit(toolInput) ?? '',
				icon: Pen,
			};
		}
	}

	return undefined;
}

/** 从 matchType 查找对应图标（供 ToolCallItem 渲染用） */
export const CAPSULE_ICON_MAP: Record<CapsuleMatchType, LucideIcon> = {
	'skill-use': Zap,
	'skill-find': Search,
	'skill-install': Download,
	'skill-community': Globe,
	'file-read': BookOpen,
	'file-write': FilePen,
	'file-rules': Pen,
	'file-memory': Pen,
	'file-soul': Pen,
	'file-rules-read': BookOpen,
	'file-memory-read': BookOpen,
	'file-soul-read': BookOpen,
	'bash-run': Terminal,
};

/** 从 Edit input 中提取简短摘要 */
function summarizeEdit(input: Record<string, unknown>): string | undefined {
	const newStr = input.new_string as string | undefined;
	if (!newStr) return undefined;
	const firstLine = newStr
		.split('\n')
		.find((l) => l.trim())
		?.trim();
	if (!firstLine) return undefined;
	return firstLine.length > 40 ? `${firstLine.slice(0, 40)}...` : firstLine;
}
